import QRCode from 'qrcode';

export interface FbrReceiptItem {
  id: string;
  qr: string;
}

const FBR_DI_LOGO_PATH = 'assets/layout/images/fbr-di-logo.png';

/** Absolute so window.open HTML can load the logo. about:blank ignores an app-relative src. */
export function fbrDiLogoAbsoluteUrl(): string {
  return new URL(FBR_DI_LOGO_PATH, document.baseURI).href;
}

/** Real ids only. Prefers split lines, then FBRInvoiceIDs, then a single FBRInvoiceID. */
export function collectFbrInvoiceIds(source: {
  FBRInvoiceLines?: string[] | null;
  FBRInvoiceIDs?: string[] | null;
  FBRInvoiceID?: string | null;
} | null | undefined): string[] {
  const lines = trimIds(source?.FBRInvoiceLines);
  if (lines.length) return lines;
  const many = trimIds(source?.FBRInvoiceIDs);
  if (many.length) return many;
  const one = String(source?.FBRInvoiceID ?? '').trim();
  return one ? [one] : [];
}

function trimIds(ids: string[] | null | undefined): string[] {
  return (ids || [])
    .map((id) => String(id ?? '').trim())
    .filter((id) => id.length > 0);
}

/**
 * QR payload is the invoice number only. Version 2, ECC M.
 * ponytail: version 2 holds 32 numeric / 16 alphanumeric chars. A longer id
 * (documented DI numbers like 7000007DI…) keeps ECC M and uses the smallest
 * symbol that fits. Upgrade path: none until FBR shortens the number.
 */
export async function fbrInvoiceQrDataUrl(invoiceId: string): Promise<string> {
  const payload = String(invoiceId).trim();
  try {
    return await QRCode.toDataURL(payload, {
      version: 2,
      errorCorrectionLevel: 'M',
      margin: 1,
    });
  } catch (err) {
    const message = String((err as Error)?.message || err);
    if (!/version/i.test(message)) throw err;
    return QRCode.toDataURL(payload, {
      errorCorrectionLevel: 'M',
      margin: 1,
    });
  }
}

export async function fbrQrItems(ids: string[]): Promise<FbrReceiptItem[]> {
  const items: FbrReceiptItem[] = [];
  for (const raw of ids || []) {
    const id = String(raw ?? '').trim();
    if (!id) continue;
    items.push({ id, qr: await fbrInvoiceQrDataUrl(id) });
  }
  return items;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Logo and QR side by side, then one line per id. Empty list prints nothing. */
export function fbrReceiptBlockHtml(items: FbrReceiptItem[], logoUrl: string): string {
  if (!items?.length) return '';
  const qrs = items
    .map(
      (item) =>
        `<img src="${item.qr}" alt="FBR QR" style="display:block;width:25mm;height:25mm;margin:0 auto 2px;" />`,
    )
    .join('');
  const lines = items
    .map(
      (item) =>
        `<div style="margin-top:2px;font-size:10px;font-weight:bold;color:black;word-break:break-all;overflow-wrap:anywhere;white-space:normal;">FBR-InvoiceID: ${escapeHtml(item.id)}</div>`,
    )
    .join('');
  return `
    <div style="text-align:center;padding-top:6px;">
      <table style="width:auto !important;margin:0 auto;border-collapse:collapse;">
        <tr>
          <td style="width:auto !important;vertical-align:middle;text-align:center !important;padding:0 3px 0 0 !important;">
            <img src="${escapeHtml(logoUrl)}" alt="FBR Digital Invoicing" style="display:block;width:22mm;height:auto;" />
          </td>
          <td style="width:auto !important;vertical-align:middle;text-align:center !important;padding:0 0 0 3px !important;">${qrs}</td>
        </tr>
      </table>
      ${lines}
    </div>`;
}
